import {
  Component,
  DestroyRef,
  inject,
  OnDestroy,
  OnInit,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { select, Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';

import { FormGroup } from '@angular/forms';
import { FormsService } from '../../services/forms.service';

import {
  BuilderType,
  SearchBuilderComponent,
  SearchBuilderData,
} from '../search-builder/search-builder.component';
import { ToolbarButton } from '../../models/toolbar.models';

import { Feed } from '../../models/core.models';

import { FeedPersistenceService } from '../../services/feed-persistence.service';

import { selectFeed } from '../../reducers';
import { AppState } from 'src/app/reducers';

import { BehaviorSubject, tap } from 'rxjs';
import { LoggerService } from '@services/logger.service';
import { NomObjectTypes } from '../../constants';
import { SidebarService } from '../../services/sidebar.service';
import { QueryHintOverlayService } from '@services/query-hint-overlay.service';
import { EditorView, keymap } from '@codemirror/view';

@Component({
  selector: 'app-search-builder-wrapper',
  templateUrl: './search-builder-wrapper.component.html',
  styleUrls: ['./search-builder-wrapper.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class SearchBuilderWrapperComponent implements OnDestroy, OnInit {
  @ViewChild('searchBuilder', { static: false })
  searchBuilder: SearchBuilderComponent;
  buttons$ = new BehaviorSubject<ToolbarButton[]>([]);
  wideSidebar: boolean = true; // will open wide sidebar by default
  feed: Feed;
  searchBuilderData: SearchBuilderData;
  builderType: BuilderType = 'editor';
  componentId: string = 'search-builder';

  NomObjectTypes = NomObjectTypes;

  private destroyRef = inject(DestroyRef);
  private logger = inject(LoggerService);
  private sidebarService = inject(SidebarService);
  private feedPersistence = inject(FeedPersistenceService);

  ctrlEnterExtension = keymap.of([
    {
      key: 'Ctrl-Enter',
      run: (view: EditorView) => {
        this.editFeed();
        return true;
      },
    },
  ]);

  constructor(
    protected store: Store<AppState>,
    protected formService: FormsService,
    protected translateService: TranslateService,
    public queryHintsOverlayService: QueryHintOverlayService,
  ) {
    this.store
      .pipe(
        // select the feed from the store
        select(selectFeed),
        // side-effect: assign to the component property
        tap((feed) => {
          this.feed = feed;
        }),
        // automatically unsubscribe on destroy
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  ngOnInit(): void {
    this.onBuilderChange(this.builderType);
  }

  updateButtons() {
    const buttons: ToolbarButton[] = [
      {
        type: 'button',
        variant: 'secondary',
        label: this.translateService.instant('btn.cancel'),
        leadingIcon: ['fas', 'times'],
        action: () => {
          this.sidebarService.closeSidebar();
        },
        shouldDisplay: this.builderType != 'history',
      },
      {
        type: 'button',
        variant: 'primary',
        label: this.translateService.instant(
          'search-builder-wrapper.button.show-hints',
        ),
        leadingIcon: ['fas', 'question'],
        action: this.showHints.bind(this),
        shouldDisplay: this.builderType == 'editor',
      },
      {
        type: 'button',
        variant: 'primary',
        label: this.translateService.instant('btn.save'),
        leadingIcon: ['fas', 'save'],
        action: this.editFeed.bind(this),
        shouldDisplay: this.builderType != 'history',
        loading: this.searchBuilder?.formGroup?.invalid,
      },
    ];

    this.buttons$.next(buttons);
  }

  onFormGroupChange(formGroup: FormGroup) {
    this.logger.debug(
      'SearchBuilderWrapperComponent: onFormGroupChange',
      formGroup.value,
    );
    this.searchBuilderData = formGroup.value;
    this.searchBuilderData.social_source_id =
      formGroup.value.post_social_source_id;
  }

  onDataChange(data) {
    // console.log('data', data);
    this.logger.debug('SearchBuilderWrapperComponent: onDataChange', data);
  }

  onBuilderChange(builder: BuilderType) {
    this.builderType = builder;
    this.updateButtons();
  }

  /**
   * Edits the feed with the provided data.
   */
  editFeed() {
    this.searchBuilder.submit();

    if (this.searchBuilder.formGroup.invalid) {
      this.logger.error(
        'SearchBuilderWrapperComponent: editFeed - Form is invalid. Please correct the errors and try again.',
      );
      this.logger.debug(
        'SearchBuilderWrapperComponent: editFeed - Form errors:',
        this.searchBuilder.formGroup.errors,
      );
      this.logger.debug(
        'SearchBuilderWrapperComponent: editFeed - Form value:',
        this.searchBuilder.formGroup.value,
      );
      return;
    }
    // backend now expects query_text instead of query
    const feedData = {
      ...this.searchBuilderData,
      feed_id: this.feed.id,
      workspace_id: this.feed.workspace_id,
      title: this.feed.title,
      title_i18n: this.feed.title_i18n,
      description: this.feed.description,
      query_text: this.searchBuilderData.query_text,
      query_created_from: this.searchBuilderData.query_created_from,
      default_period_in_days: this.feed.default_period_in_days,
      is_screen_capture_enabled: this.feed.is_screen_capture_enabled,
      filter_queries: this.feed.filter_queries,
      company_categories: this.feed.company_categories,
      cache_enabled: this.feed.cache_enabled,
      is_paused: this.feed.is_paused,
      is_period_locked: this.feed.is_period_locked,
      workflow_id: this.feed.workflow_id,
      default_color: this.feed.default_color,
      is_auto_refresh: this.feed.is_auto_refresh,
      auto_refresh_seconds: this.feed.auto_refresh_seconds,
      default_export_language_id: this.feed.default_export_language_id,
      auto_face_recognition_enabled: this.feed.auto_face_recognition_enabled,
      is_live_stream_enabled: this.feed.is_live_stream_enabled,
      params: {
        social_sources: this.searchBuilderData.social_source_id,
        combined_feeds: this.searchBuilderData.combined_feeds,
        social_facebook_comments_status:
          this.searchBuilderData.social_facebook_comments_status,
      },
    };

    this.feedPersistence.edit(this.feed.id, feedData);
  }

  showHints() {
    this.queryHintsOverlayService.open(this.componentId);
  }

  ngOnDestroy() {
    this.queryHintsOverlayService.closeIfOwner(this.componentId);
  }
}
